"use client";
import React, { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "@/utils/dayjs";
import { WeatherForecastData } from "@/types/forecast";
import {
  buildDaySegments,
  PRECIP_GLYPHS,
  TimelineSegment,
} from "@/app/(pages)/Home/components/shared/Highcharts/components/WeatherTimeline";
import { formatMetricDeltaAbs } from "@/metrics/registry";
import { getCityClockFromDay } from "@/utils/cityTime";
import cx from "clsx";

type Props = {
  weather: WeatherForecastData;
  activeDay: string;
  isMobile?: boolean;
  callback?: (s: any) => void;
};

export type Model = {
  phrase: string;
  chip: string;
  chipTone: "cold" | "warm";
};

const GUSTS_THRESHOLD = 30;

const fmtHour = (h: number) => `${String(h).padStart(2, "0")}:00`;

const toNumbers = (raw: unknown): number[] =>
  Array.isArray(raw)
    ? raw
        .map((v) => (typeof v === "number" ? v : Number(v)))
        .filter(Number.isFinite)
    : [];

const dayStr = (date: string) => dayjs(date).format("YYYY-MM-DD");

const HeroStrip = ({
  weather,
  activeDay,
  isMobile = true,
  callback,
}: Props) => {
  const { t, i18n } = useTranslation();
  const day = weather[activeDay];

  const model: Model = useMemo(() => {
    if (!day) return null;

    const { hour: currentHour, todayKey } = getCityClockFromDay(day);
    const isToday = dayStr(day.date) === todayKey;

    const describe = (code: number) => t(`weather.${code}.description`);
    const lower = (text: string) =>
      text.charAt(0).toLocaleLowerCase(i18n.language) + text.slice(1);

    const dayTemps = toNumbers(day.temperature);
    const dayMax = dayTemps.length ? Math.floor(Math.max(...dayTemps)) : null;
    const dayGusts = toNumbers(day.windGusts);
    const daySpeeds = toNumbers(day.windSpeed);
    const dayWindPeak = dayGusts.length
      ? Math.floor(Math.max(...dayGusts))
      : daySpeeds.length
        ? Math.floor(Math.max(...daySpeeds))
        : null;
    const dayWindAvg = daySpeeds.length
      ? Math.round(daySpeeds.reduce((s, v) => s + v, 0) / daySpeeds.length)
      : null;
    const noPrecipPhrase = (code: number) =>
      dayMax != null && dayWindPeak != null && dayWindAvg != null
        ? t("hero.no_precip_full", {
            cond: describe(code),
            max: dayMax,
            avg: dayWindAvg,
            peak: dayWindPeak,
          })
        : dayMax != null
          ? t("hero.no_precip_temp", { cond: describe(code), max: dayMax })
          : t("hero.no_precip", { cond: describe(code) });

    const segments = buildDaySegments(day);
    const precip = segments.filter((s) => PRECIP_GLYPHS.includes(s.glyph));

    const dominant = segments.reduce<TimelineSegment | null>(
      (acc, s) => (!acc || s.to - s.from > acc.to - acc.from ? s : acc),
      null,
    );

    let phrase: string | null = null;
    if (isToday) {
      const active = precip.find(
        (s) => currentHour >= s.from && currentHour <= s.to,
      );
      const upcoming = precip.find((s) => s.from > currentHour);
      if (active) {
        const endHour = Math.min(active.to + 1, 23);
        const next = segments.find((s) => s.from > active.to);
        phrase = next
          ? t("hero.precip_ends", {
              what: describe(active.code),
              time: fmtHour(endHour),
              next: lower(describe(next.code)),
            })
          : t("hero.precip_ends_plain", {
              what: describe(active.code),
              time: fmtHour(endHour),
            });
      } else if (upcoming) {
        phrase = t("hero.precip_starts", {
          what: lower(describe(upcoming.code)),
          time: fmtHour(upcoming.from),
        });
      } else if (dominant) {
        phrase = noPrecipPhrase(dominant.code);
      }
    } else if (precip.length) {
      phrase = t("hero.precip_window", {
        what: describe(precip[0].code),
        from: fmtHour(precip[0].from),
        to: fmtHour(Math.min(precip[0].to + 1, 23)),
      });
    } else if (dominant) {
      phrase = noPrecipPhrase(dominant.code);
    }

    const gusts = toNumbers(day.windGusts);
    if (gusts.length) {
      let maxIdx = 0;
      gusts.forEach((v, i) => {
        if (v > gusts[maxIdx]) maxIdx = i;
      });
      const showGusts =
        gusts[maxIdx] >= GUSTS_THRESHOLD && (!isToday || maxIdx > currentHour);
      if (showGusts) {
        const when =
          maxIdx < 12
            ? t("hero.morning")
            : maxIdx < 18
              ? t("hero.afternoon")
              : t("hero.evening");
        const gustsSentence = t("hero.gusts", {
          when,
          value: `${Math.floor(gusts[maxIdx])} ${t("measures.km")}`,
          interpolation: { escapeValue: false },
        });

        phrase = phrase ? `${phrase} ${gustsSentence}` : gustsSentence;
      }
    }

    const tempAtHour = (d?: WeatherForecastData[string]) => {
      const arr = toNumbers(d?.temperature);
      if (!arr.length) return null;
      return arr[Math.min(currentHour, arr.length - 1)];
    };
    const todayData = Object.values(weather).find(
      (d) => dayStr(d.date) === todayKey,
    );

    let chip: string | null = null;
    let chipTone: "cold" | "warm" | null = null;
    if (isToday) {
      const rawDelta = day.tempVsPrev;
      if (typeof rawDelta === "number" && Number.isFinite(rawDelta)) {
        const rounded = Math.round(rawDelta);
        if (rounded === 0) {
          chip = t("hero.same_yesterday");
        } else {
          chipTone = rounded < 0 ? "cold" : "warm";
          chip = t(rounded < 0 ? "hero.colder" : "hero.warmer", {
            value: formatMetricDeltaAbs("temperature", rawDelta, t),
          });
        }
      }
    } else {
      const nowTemp = tempAtHour(todayData);
      const thenTemp = tempAtHour(day);
      if (nowTemp != null && thenTemp != null) {
        const diff = thenTemp - nowTemp;
        const value = formatMetricDeltaAbs("temperature", diff, t);
        if (Math.round(diff) === 0) {
          chip = t("hero.same_as_now");
        } else {
          chipTone = diff < 0 ? "cold" : "warm";
          chip =
            Math.abs(diff) >= 5
              ? t(diff < 0 ? "hero.much_colder_now" : "hero.much_warmer_now", {
                  value,
                })
              : t(diff < 0 ? "hero.colder_now" : "hero.warmer_now", { value });
        }
      }
    }

    if (!chip) {
      const temps = toNumbers(day.temperature);
      if (temps.length) {
        chip = t("hero.day_range", {
          max: Math.floor(Math.max(...temps)),
          min: Math.floor(Math.min(...temps)),
        });
      }
    }

    if (!phrase && !chip) return null;
    return { phrase, chip, chipTone };
  }, [day, t, i18n.language]);

  if (!model) return null;

  useEffect(() => {
    callback && callback(model);
  }, [model]);

  return (
    <section
      aria-label={t("hero.aria")}
      className={cx("relative z-50 w-full", isMobile && "mb-4")}
    >
      <div
        className={cx(
          "relative flex flex-wrap items-center gap-x-2 gap-y-1.5",
          isMobile && "p-4",
        )}
      >
        <span
          aria-hidden
          className={cx("text-soft-purple shrink-0", !isMobile && "hidden")}
        >
          <svg width={18} height={18} viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M12 2l1.5 6.5L20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5z"
            />
          </svg>
        </span>
        {model.chip && isMobile && (
          <span
            className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[13px] ${
              model.chipTone === "cold"
                ? "border-[#5ab4f0]/40 text-[#7cc5f5]"
                : model.chipTone === "warm"
                  ? "border-[#f5a34f]/45 text-[#f7b56b]"
                  : "border-white/25"
            }`}
          >
            {model.chip}
          </span>
        )}
        {model.phrase && (
          <p
            className={cx(
              "min-w-0 text-sm leading-5 text-white/60",
              isMobile && "text-white/80",
            )}
          >
            {model.phrase}
          </p>
        )}
        <div
          className={cx(
            "surface-card absolute top-0 left-0 -z-1 h-full w-full rounded-3xl",
            !isMobile && "hidden",
          )}
        />
      </div>
    </section>
  );
};

export default memo(HeroStrip);
